import { apiRequest, apiDownload } from './client.js';

/**
 * Advanced Reports API (ADR-0027). Typed wrappers around /v1/reports/*.
 * Contract: apps/api/src/routes/reports.ts + services/reportsQuery.ts.
 * Entitlement-gated (advanced_reports) — a Starter tenant gets 402 PLAN_REQUIRED,
 * which the pages render as the upgrade prompt via <ApiErrorNotice>.
 */

export interface ReportFilters {
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;
  department?: string;
  country?: string;
}

export interface HeadcountRow {
  group: string | null;
  headcountStart: number;
  headcountEnd: number;
  averageHeadcount: number;
  newHires: number;
  terminations: number;
  turnoverRate: number | null;
}
export interface HeadcountReport {
  meta: { periodStart: string; periodEnd: string; department?: string; countryCode?: string; turnoverNote: string };
  total: HeadcountRow;
  byDepartment: HeadcountRow[];
  byCountry: HeadcountRow[];
}

export interface CostRow {
  currency: string;
  group: string | null;
  gross: string;
  tax: string;
  socialSecurity: string;
  otherDeductions: string;
  net: string;
  employeeCount: number;
}
/** FX consolidation (ADR-0031) — what the base-currency roll-up left out. */
export interface Unconsolidated {
  count: number;
  byCurrency: Array<{ currency: string; count: number; gross: string }>;
}
/** The consolidated basis line — VERBATIM the same wording the exports use. */
export const CONSOLIDATED_BASIS_NOTE = "Consolidated at each payslip's pay-date exchange rate, as currently recorded (8dp); per-currency figures above are authoritative.";
/** One disclosure line per unconsolidated currency (D5) — rendered only when non-zero. */
export function unconsolidatedNotes(u: Unconsolidated | undefined, base: string): string[] {
  if (!u || u.count === 0) return [];
  return u.byCurrency.map((x) => `${x.count} payslip${x.count === 1 ? '' : 's'} totalling ${x.gross} ${x.currency} could not be converted — no ${x.currency} to ${base} rate on the pay date.`);
}
export interface PayrollCostReport {
  meta: { periodStart: string; periodEnd: string; department?: string; countryCode?: string; headline: string; basisNote: string; filterNote?: string; unconsolidated?: Unconsolidated };
  byCurrency: Array<Omit<CostRow, 'group'>>;
  byDepartment: CostRow[];
  byCountry: CostRow[];
  /** Additive base-currency roll-up; present only when the report spans >1 currency. */
  consolidated?: { baseCurrency: string; total: Omit<CostRow, 'group' | 'currency'>; byDepartment: CostRow[]; byCountry: CostRow[] };
}

// --- Leave Liability (ADR-0028) — an AS-OF snapshot, so an `asOf` date, not a period.
export interface LiabilityFilters {
  asOf: string; // YYYY-MM-DD
  department?: string;
  country?: string;
}
export interface LiabilityRow {
  currency: string;
  group: string | null;
  liability: string; // fixed-4
  unusedDays: string; // fixed-2
  employeeCount: number;
}
export interface LeaveLiabilityReport {
  meta: {
    asOf: string;
    department?: string;
    countryCode?: string;
    headline: string;
    basisNote: string;
    filterNote?: string;
    /** Whole population active on asOf (incl. unvalued). */
    employeeCount: number;
    /** Of those, how many had no compensation on asOf and contributed 0 (D7). */
    noCompensationCount: number;
  };
  byCurrency: Array<Omit<LiabilityRow, 'group'>>;
  byDepartment: LiabilityRow[];
  byCountry: LiabilityRow[];
}

// --- Absence (ADR-0029) — a period-flow of leave taken (days, not money).
export interface AbsenceRow {
  group: string | null;
  days: string; // fixed-2
  requestCount: number;
}
export interface AbsenceReport {
  meta: { periodStart: string; periodEnd: string; department?: string; countryCode?: string; headline: string; basisNote: string; filterNote?: string };
  total: { days: string; requestCount: number };
  byType: AbsenceRow[];
  byDepartment: AbsenceRow[];
  byCountry: AbsenceRow[];
}

// --- Cost to Company (ADR-0030) — gross + employer statutory contributions.
export interface CtcRow {
  currency: string;
  group: string | null;
  gross: string;
  employerContributions: string;
  costToCompany: string;
  employeeCount: number;
}
export interface CostToCompanyReport {
  meta: {
    periodStart: string;
    periodEnd: string;
    department?: string;
    countryCode?: string;
    headline: string;
    basisNote: string;
    filterNote?: string;
    /** Payslips with an unreadable employer figure (contributed 0); a data-integrity signal. */
    malformedEmployerCount: number;
    unconsolidated?: Unconsolidated;
  };
  byCurrency: Array<Omit<CtcRow, 'group'>>;
  byDepartment: CtcRow[];
  byCountry: CtcRow[];
  /** Additive base-currency roll-up; present only when the report spans >1 currency. */
  consolidated?: { baseCurrency: string; total: Omit<CtcRow, 'group' | 'currency'>; byDepartment: CtcRow[]; byCountry: CtcRow[] };
}

function qs(f: ReportFilters, extra?: Record<string, string>): string {
  const p = new URLSearchParams({ periodStart: f.periodStart, periodEnd: f.periodEnd });
  if (f.department) p.set('department', f.department);
  if (f.country) p.set('country', f.country);
  for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
  return p.toString();
}
function liabQs(f: LiabilityFilters, extra?: Record<string, string>): string {
  const p = new URLSearchParams({ asOf: f.asOf });
  if (f.department) p.set('department', f.department);
  if (f.country) p.set('country', f.country);
  for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
  return p.toString();
}

export function getHeadcountReport(f: ReportFilters): Promise<HeadcountReport> {
  return apiRequest<HeadcountReport>(`/v1/reports/headcount?${qs(f)}`);
}
export function getPayrollCostReport(f: ReportFilters): Promise<PayrollCostReport> {
  return apiRequest<PayrollCostReport>(`/v1/reports/payroll-cost?${qs(f)}`);
}
export function getLeaveLiabilityReport(f: LiabilityFilters): Promise<LeaveLiabilityReport> {
  return apiRequest<LeaveLiabilityReport>(`/v1/reports/leave-liability?${liabQs(f)}`);
}
export function getAbsenceReport(f: ReportFilters): Promise<AbsenceReport> {
  return apiRequest<AbsenceReport>(`/v1/reports/absence?${qs(f)}`);
}
export function getCostToCompanyReport(f: ReportFilters): Promise<CostToCompanyReport> {
  return apiRequest<CostToCompanyReport>(`/v1/reports/cost-to-company?${qs(f)}`);
}

/** Download a period-based report export. `format` is 'csv' | 'pdf'; the CSV opens in Excel. */
export function downloadReport(report: 'headcount' | 'payroll-cost' | 'absence' | 'cost-to-company', f: ReportFilters, format: 'csv' | 'pdf'): Promise<void> {
  return apiDownload(`/v1/reports/${report}?${qs(f, { format })}`);
}
/** Download the as-of leave-liability export. */
export function downloadLeaveLiability(f: LiabilityFilters, format: 'csv' | 'pdf'): Promise<void> {
  return apiDownload(`/v1/reports/leave-liability?${liabQs(f, { format })}`);
}
